import React, { useState, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Monitor,
  Smartphone,
  ChevronLeft,
  ChevronRight,
  List,
  Plus,
  ExternalLink,
  Edit2,
  Save,
  X,
  Loader2,
  Image as ImageIcon,
  Trash2,
} from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/components/ui/use-toast';
import { trpc } from '@/lib/api/trpc';
import { Card, CardContent } from '@/components/ui/card';
import { DemoPreviews, PluginPage, PreviewItem, PagePreview } from './types';

interface PluginPreviewViewerProps {
  className?: string;
  editable?: boolean;
  deploymentId: string; // Required - the component will fetch its own data
  viewMode?: 'desktop' | 'mobile'; // Controlled view mode from parent
  onViewModeChange?: (mode: 'desktop' | 'mobile') => void; // Callback to update parent state
  selectedPreset?: string; // Selected config preset to append to preview URLs
  deploymentPages?: PluginPage[]; // All pages available in the deployment (for page selector)
}

/**
 * Independent preview viewer component for plugin screenshots
 * Fetches its own data and manages its own state
 * Shows desktop and mobile views with browser simulation
 * Supports edit mode for owners to add/manage preview URLs
 */
export function PluginPreviewViewer({
  className = '',
  editable = false,
  deploymentId,
  viewMode: controlledViewMode,
  onViewModeChange,
  selectedPreset = 'default',
  deploymentPages = [],
}: PluginPreviewViewerProps) {
  const { t } = useTranslation(['storefront', 'common']);
  const { toast } = useToast();
  const utils = trpc.useUtils();

  // Helper function to append preset to preview URL
  const getPreviewUrlWithPreset = (baseUrl: string): string => {
    if (!selectedPreset || selectedPreset === 'default') return baseUrl;

    try {
      const url = new URL(baseUrl);
      url.searchParams.set('preset', selectedPreset);
      return url.toString();
    } catch (e) {
      // If URL parsing fails, just return the original
      return baseUrl;
    }
  };

  // State must be declared before useQuery that depends on it
  const [isCapturing, setIsCapturing] = useState(false); // Optimistic loading state

  // Fetch preview data independently
  const { data: previewData, isLoading: isLoadingPreviews } = trpc.plugins.getDemoPreviews.useQuery(
    { deploymentId },
    {
      enabled: !!deploymentId,
      refetchOnMount: 'always', // Always refetch when component mounts (switching versions)
      refetchOnWindowFocus: true, // Refetch when window regains focus
      staleTime: 0, // Consider data immediately stale to ensure fresh data on mount
      refetchInterval: (queryData) => {
        // Auto-refresh every 3 seconds if there are pending previews OR if we're in capturing state
        // More frequent checks to show screenshots as soon as they're ready
        const hasPending =
          queryData &&
          'pending' in queryData &&
          Array.isArray(queryData.pending) &&
          queryData.pending.length > 0;

        // Poll if there are pending previews or if we're in optimistic capturing state
        return hasPending || isCapturing ? 3000 : false;
      },
      // Force network request on every refetch, bypassing cache
      refetchOnReconnect: true,
    },
  );

  const demoPreviews = previewData?.demoPreviews;

  // Use controlled view mode if provided, otherwise use internal state
  const [internalViewMode, setInternalViewMode] = useState<'desktop' | 'mobile'>('desktop');
  const viewMode = controlledViewMode ?? internalViewMode;
  const setViewMode = (mode: 'desktop' | 'mobile') => {
    if (onViewModeChange) {
      onViewModeChange(mode);
    } else {
      setInternalViewMode(mode);
    }
  };
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const mobileScrollRef = useRef<HTMLDivElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [editPreviewUrl, setEditPreviewUrl] = useState('');
  const [selectedPageForAdd, setSelectedPageForAdd] = useState('');
  // Track current page path separately to maintain page even when no preview exists for preset
  const [currentPagePath, setCurrentPagePath] = useState<string | null>(null);

  // Add/update preview mutation
  const addPreviewMutation = trpc.plugins.addDemoPreview.useMutation({
    onSuccess: async (data) => {
      // Clear form state
      setEditPreviewUrl('');
      setSelectedPageForAdd('');

      // Close edit mode FIRST to show the preview area
      setIsEditing(false);

      // Then immediately show capturing state in the preview area
      setIsCapturing(true);

      // Only invalidate and refetch the specific deployment's previews
      // No need to invalidate the entire plugin list
      if (deploymentId) {
        await utils.plugins.getDemoPreviews.invalidate({ deploymentId });
        const freshData = await utils.plugins.getDemoPreviews.fetch({ deploymentId });

        // Find the newly added preview by its ID and set currentPageIndex to it
        if (data.previewId && freshData?.demoPreviews?.pages) {
          const newPreviewIndex = freshData.demoPreviews.pages
            .flatMap((page: PagePreview) => page.previews.map((preview: PreviewItem) => preview.id))
            .indexOf(data.previewId);

          if (newPreviewIndex !== -1) {
            setCurrentPageIndex(newPreviewIndex);
          }
        }
      }

      toast({
        title: t('common:success'),
        description: t('plugins.preview.toast.captureStarted'),
      });
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message,
        variant: 'destructive',
      });
      setIsCapturing(false);
      // Don't close edit mode on error so user can try again
    },
  });

  // Remove preview mutation
  const removePreviewMutation = trpc.plugins.removeDemoPreview.useMutation({
    onSuccess: async () => {
      toast({
        title: t('plugins.preview.toast.removedTitle'),
        description: t('plugins.preview.toast.removedDesc'),
      });

      // Clear capturing state immediately
      setIsCapturing(false);

      // Reset to first preview index
      setCurrentPageIndex(0);

      // Only invalidate the specific deployment's previews, not the entire plugin list
      if (deploymentId) {
        await utils.plugins.getDemoPreviews.invalidate({ deploymentId });
        await utils.plugins.getDemoPreviews.fetch({ deploymentId });
      }
    },
    onError: (error) => {
      toast({
        title: t('common:error'),
        description: error.message || t('plugins.preview.toast.failedRemove'),
        variant: 'destructive',
      });
      // Clear capturing state on error too
      setIsCapturing(false);
    },
  });

  // Flatten all previews with page context
  const allPreviews = demoPreviews?.pages
    ? demoPreviews.pages.flatMap((page: PagePreview, pageIdx: number) =>
        page.previews.map((preview: PreviewItem, previewIdx: number) => ({
          page,
          preview,
          pageIndex: pageIdx,
          previewIndex: previewIdx,
        })),
      )
    : [];

  const currentItem = allPreviews[currentPageIndex];

  // Get available pages for the add form from demoPreviews
  // If no pages exist yet, provide a default "/" path option
  const availablePages = demoPreviews?.pages?.length ? demoPreviews.pages : [{ path: '/', previews: [] }];

  // Initialize currentPagePath on first render if we have data
  useEffect(() => {
    if (!currentPagePath && currentItem?.page?.path) {
      setCurrentPagePath(currentItem.page.path);
    }
  }, [currentItem, currentPagePath]);

  // Initialize edit URL when entering edit mode (only when isEditing becomes true)
  // We intentionally only depend on isEditing to avoid resetting the input on every keystroke
  const isEditingRef = useRef(false);
  useEffect(() => {
    // Only initialize when transitioning to edit mode
    if (isEditing && !isEditingRef.current) {
      isEditingRef.current = true;
      if (currentItem) {
        // Set initial URL from current preview
        setEditPreviewUrl(currentItem.preview?.previewUrl || '');
      } else if (availablePages.length > 0 && !selectedPageForAdd) {
        // Pre-select first page when adding a new preview
        setSelectedPageForAdd(availablePages[0].path);
      }
    } else if (!isEditing) {
      isEditingRef.current = false;
    }
  }, [isEditing, currentItem, availablePages, selectedPageForAdd]);

  // Handler for adding/updating preview
  const handleSavePreview = async () => {
    if (!deploymentId || !editPreviewUrl) {
      toast({
        title: t('common:error'),
        description: t('plugins.preview.toast.urlRequired'),
        variant: 'destructive',
      });
      return;
    }

    const targetPath = currentItem?.page.path || selectedPageForAdd;
    if (!targetPath) {
      toast({
        title: t('common:error'),
        description: t('plugins.preview.toast.pageRequired'),
        variant: 'destructive',
      });
      return;
    }

    // Set capturing state immediately to show loading
    setIsCapturing(true);

    // If editing an existing preview, remove it first
    if (currentItem?.preview?.id) {
      try {
        // Remove the old preview
        await removePreviewMutation.mutateAsync({
          deploymentId,
          path: targetPath,
          previewId: currentItem.preview.id,
        });
      } catch (error) {
        console.error('Failed to remove old preview:', error);
        setIsCapturing(false);
        // Continue anyway - the add might work
      }
    }

    // Add the new preview with metadata including preset
    const metadata = {
      preset: selectedPreset,
      ...(selectedPreset !== 'default' && { presetName: selectedPreset }),
    };

    addPreviewMutation.mutate({
      deploymentId,
      path: targetPath,
      previewUrl: editPreviewUrl,
      metadata,
    });
  };

  const handleCancelEdit = () => {
    setIsEditing(false);
    setEditPreviewUrl('');
    setSelectedPageForAdd('');
  };

  const handleRemovePendingPreview = () => {
    if (!deploymentId || !preview?.id || !page?.path) return;

    removePreviewMutation.mutate({
      deploymentId,
      path: page.path,
      previewId: preview.id,
    });
  };

  // Determine current state
  const hasData = demoPreviews && demoPreviews.pages && demoPreviews.pages.length > 0;
  const hasCurrentPreview = !!currentItem;

  // Get unique pages for the page selector first
  // Merge deployment pages with pages that have previews
  const uniquePages = Array.from(
    new Map(
      allPreviews.map((item: { page: PagePreview; preview: PreviewItem }) => [item.page.path, item]),
    ).values(),
  );

  // Create a comprehensive page list including all deployment pages
  const allAvailablePages = (() => {
    const pagesMap = new Map<
      string,
      { page: PluginPage; hasPreview: boolean; hasPreviewForPreset: boolean; previewItem?: any }
    >();

    // Add all deployment pages first
    deploymentPages.forEach((deploymentPage) => {
      pagesMap.set(deploymentPage.path, {
        page: deploymentPage,
        hasPreview: false,
        hasPreviewForPreset: false,
      });
    });

    // Then add/update with pages that have previews
    uniquePages.forEach((item: { page: PagePreview; preview: PreviewItem }) => {
      // Treat previews without metadata.preset as "default"
      const hasPreviewForCurrentPreset = allPreviews.some(
        (p: { page: PagePreview; preview: PreviewItem }) => {
          const previewPreset = p.preview.metadata?.preset || 'default';
          return p.page.path === item.page.path && previewPreset === selectedPreset;
        },
      );

      pagesMap.set(item.page.path, {
        page: { path: item.page.path, name: item.page.name } as PluginPage,
        hasPreview: true,
        hasPreviewForPreset: hasPreviewForCurrentPreset,
        previewItem: item,
      });
    });

    return Array.from(pagesMap.values());
  })();

  // Calculate derived values before hooks
  // Prioritize: currentItem > currentPagePath > selectedPageForAdd > first available page
  const { page, preview } = (() => {
    if (currentItem) {
      return currentItem;
    }

    // If we have a tracked page path (for when no preview exists for current preset)
    if (currentPagePath) {
      const pageFromPath =
        allAvailablePages.find((p) => p.page.path === currentPagePath)?.page ||
        deploymentPages.find((p) => p.path === currentPagePath);
      if (pageFromPath) {
        return { page: pageFromPath, preview: null };
      }
    }

    // If adding a new preview
    if (selectedPageForAdd) {
      const pageFromAdd =
        allAvailablePages.find((p) => p.page.path === selectedPageForAdd)?.page ||
        deploymentPages.find((p) => p.path === selectedPageForAdd);
      if (pageFromAdd) {
        return { page: pageFromAdd, preview: null };
      }
    }

    // Fallback to first available page
    return { page: availablePages[0], preview: null };
  })();
  const screenshotUrl = preview
    ? viewMode === 'desktop'
      ? preview.screenshotUrls?.desktop
      : preview.screenshotUrls?.mobile
    : null;

  // Check if we're in capturing state (optimistic or actual)
  const isPendingCapture = isCapturing || preview?.metadata?.status === 'pending';

  // Get all previews for the current page (different presets)
  const currentPagePreviews = page
    ? allPreviews.filter((item: { page: PagePreview; preview: PreviewItem }) => item.page.path === page.path)
    : [];
  const currentPresetIndex = preview
    ? currentPagePreviews.findIndex(
        (item: { page: PagePreview; preview: PreviewItem }) => item.preview.id === preview.id,
      )
    : -1;

  // Validate that the current preview matches the selected preset
  useEffect(() => {
    if (!preview || currentPageIndex < 0 || !allPreviews.length) return;

    const previewPreset = preview.metadata?.preset || 'default';

    if (previewPreset !== selectedPreset && page?.path) {
      // Force the preset effect to run and find the correct preview
      prevPresetRef.current = '___FORCE_RERUN___';
    }
  }, [preview, selectedPreset, currentPageIndex, allPreviews.length, page?.path]);

  const handlePageChange = (selectedPath: string) => {
    // Always update the current page path to maintain page context
    setCurrentPagePath(selectedPath);

    // First, try to find a preview for the selected page that matches the current preset
    const previewsForPage = allPreviews.filter(
      (item: { page: PagePreview; preview: PreviewItem }) => item.page.path === selectedPath,
    );

    // Look for preview with matching preset in metadata
    // Treat previews without metadata.preset as "default"
    const matchingPreviewIndex = previewsForPage.findIndex(
      (item: { page: PagePreview; preview: PreviewItem }) => {
        const previewPreset = item.preview.metadata?.preset || 'default';
        return previewPreset === selectedPreset;
      },
    );

    // If found, use that preview
    if (matchingPreviewIndex !== -1) {
      const globalIndex = allPreviews.findIndex(
        (item: { page: PagePreview; preview: PreviewItem }) =>
          item.page.path === selectedPath &&
          item.preview.id === previewsForPage[matchingPreviewIndex].preview.id,
      );
      if (globalIndex !== -1) {
        setCurrentPageIndex(globalIndex);
        setIsEditing(false);
        return;
      }
    }

    // Otherwise, use the first preview for the page (if any)
    const firstPreviewOfPage = previewsForPage[0];
    if (firstPreviewOfPage) {
      const globalIndex = allPreviews.findIndex(
        (item: { page: PagePreview; preview: PreviewItem }) =>
          item.page.path === firstPreviewOfPage.page.path &&
          item.preview.id === firstPreviewOfPage.preview.id,
      );
      if (globalIndex !== -1) {
        setCurrentPageIndex(globalIndex);
        setIsEditing(false);
        return;
      }
    }

    // If no preview exists for this page, show the "no preview" state
    // Don't automatically enter edit mode - let the user click "Add Preview"
    setSelectedPageForAdd(selectedPath);
    setCurrentPageIndex(-1); // Set to -1 to indicate no preview
    setIsEditing(false);
  };

  const handlePrevious = () => {
    if (!page) return;
    const currentPageIndexInUnique = uniquePages.findIndex(
      (item: { page: PagePreview; preview: PreviewItem }) => item.page.path === page.path,
    );
    const prevPageIndex =
      currentPageIndexInUnique > 0 ? currentPageIndexInUnique - 1 : uniquePages.length - 1;
    const prevPage = uniquePages[prevPageIndex];
    if (prevPage) {
      handlePageChange(prevPage.page.path);
    }
  };

  const handleNext = () => {
    if (!page) return;
    const currentPageIndexInUnique = uniquePages.findIndex(
      (item: { page: PagePreview; preview: PreviewItem }) => item.page.path === page.path,
    );
    const nextPageIndex =
      currentPageIndexInUnique < uniquePages.length - 1 ? currentPageIndexInUnique + 1 : 0;
    const nextPage = uniquePages[nextPageIndex];
    if (nextPage) {
      handlePageChange(nextPage.page.path);
    }
  };

  const handlePresetChange = (presetIndexInPage: number) => {
    const selectedPreview = currentPagePreviews[presetIndexInPage];
    const globalIndex = allPreviews.findIndex(
      (item: { page: PagePreview; preview: PreviewItem }) =>
        item.page.path === selectedPreview.page.path && item.preview.id === selectedPreview.preview.id,
    );
    if (globalIndex !== -1) {
      setCurrentPageIndex(globalIndex);
    }
  };

  // Reset scroll to top when page changes
  useEffect(() => {
    if (mobileScrollRef.current) {
      mobileScrollRef.current.scrollTop = 0;
    }
  }, [currentPageIndex, viewMode]);

  // Track previous selectedPreset to detect changes
  const prevPresetRef = useRef(selectedPreset);

  // When selectedPreset changes, try to find a preview for the current page with that preset
  useEffect(() => {
    // Only run if preset actually changed (not on initial mount or other re-renders)
    if (prevPresetRef.current === selectedPreset) {
      return;
    }
    prevPresetRef.current = selectedPreset;

    if (!page?.path || !allPreviews.length) {
      return;
    }

    // Find all previews for the current page
    const previewsForCurrentPage = allPreviews.filter(
      (item: { page: PagePreview; preview: PreviewItem }) => item.page.path === page.path,
    );

    // Look for a preview matching the selected preset
    // Treat previews without metadata.preset as "default"
    const matchingPreview = previewsForCurrentPage.find(
      (item: { page: PagePreview; preview: PreviewItem }) => {
        const previewPreset = item.preview.metadata?.preset || 'default';
        return previewPreset === selectedPreset;
      },
    );

    // Always update currentPagePath to maintain page context
    setCurrentPagePath(page.path);

    if (matchingPreview) {
      // Found a preview for this preset, switch to it
      const globalIndex = allPreviews.findIndex(
        (item: { page: PagePreview; preview: PreviewItem }) =>
          item.page.path === page.path && item.preview.id === matchingPreview.preview.id,
      );
      if (globalIndex !== -1 && globalIndex !== currentPageIndex) {
        setCurrentPageIndex(globalIndex);
      }
    } else {
      // No preview exists for this preset on current page
      // Show "no preview" state by setting index to -1
      if (previewsForCurrentPage.length > 0) {
        // There are previews for this page, but not for this preset
        setCurrentPageIndex(-1);
        setSelectedPageForAdd(page.path);
      }
    }
  }, [selectedPreset, page?.path, allPreviews]);

  // Keyboard navigation - navigate between unique pages
  useEffect(() => {
    if (uniquePages.length <= 1 || isEditing) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        handlePrevious();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        handleNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [uniquePages.length, page?.path, isEditing, handlePrevious, handleNext]);

  // Reset capturing state when screenshots are actually captured or preview is removed
  useEffect(() => {
    if (isCapturing) {
      // If no previews exist at all (empty pages array), clear capturing immediately
      if (!hasData) {
        setIsCapturing(false);
        return;
      }

      // If no current preview (it was removed), clear capturing
      if (!preview) {
        setIsCapturing(false);
        return;
      }

      // Only clear optimistic capturing state if we have actual screenshots
      // or if the status is no longer pending
      const hasScreenshots = preview.screenshotUrls?.desktop || preview.screenshotUrls?.mobile;
      const notPending = preview.metadata?.status && preview.metadata.status !== 'pending';

      if (hasScreenshots || notPending) {
        setIsCapturing(false);
      }
    }
  }, [preview, isCapturing, hasData]);

  // Handle loading state
  if (isLoadingPreviews) {
    return (
      <div
        className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 ${className}`}
      >
        <div className="flex flex-col items-center gap-4 text-center">
          <div className="bg-primary/10 flex h-20 w-20 items-center justify-center rounded-full">
            <Loader2 className="text-primary h-10 w-10 animate-spin" />
          </div>
          <div>
            <h3 className="font-semibold">{t('plugins.preview.loading')}</h3>
            <p className="text-muted-foreground mt-1 text-sm">{t('plugins.preview.loadingSubtitle')}</p>
          </div>
        </div>
      </div>
    );
  }

  // Handle empty state (only show if not editing)
  if (!hasData && !isEditing) {
    return (
      <div
        className={`flex h-full w-full items-center justify-center bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-950 ${className}`}
      >
        <div className="max-w-md px-6 text-center">
          <div className="from-muted/50 to-muted/20 mx-auto mb-5 flex h-20 w-20 items-center justify-center rounded-full bg-gradient-to-br">
            <Monitor className="text-muted-foreground h-10 w-10" />
          </div>
          <h3 className="mb-2 text-xl font-bold">{t('plugins.preview.comingSoonTitle')}</h3>
          <p className="text-muted-foreground text-sm leading-relaxed">
            {selectedPreset !== 'default'
              ? t('plugins.preview.comingSoonForStyle', { style: selectedPreset })
              : t('plugins.preview.comingSoonDefault')}
          </p>
          {editable && (
            <Button
              onClick={() => {
                if (page?.path) {
                  setSelectedPageForAdd(page.path);
                }
                setIsEditing(true);
              }}
              className="mt-6 gap-2"
              size="sm"
            >
              <Plus className="h-4 w-4" />
              {t('plugins.preview.generateNow')}
            </Button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={`flex h-full w-full flex-col ${className}`}>
      {/* Header */}
      <div className="flex shrink-0 items-center justify-between gap-4 border-b px-4 py-3">
        <div className="flex items-center gap-3">
          {/* Page Switcher - Show on left - Always visible if deployment has pages */}
          {!isEditing && allAvailablePages.length > 0 && (
            <Select
              value={page?.path || selectedPageForAdd || allAvailablePages[0]?.page.path || ''}
              onValueChange={handlePageChange}
            >
              <SelectTrigger className="h-8 w-[220px] text-xs">
                <div className="flex items-center gap-2">
                  <List className="h-3 w-3" />
                  <SelectValue placeholder={t('plugins.preview.pageSelectPlaceholder')} />
                </div>
              </SelectTrigger>
              <SelectContent>
                {allAvailablePages.map((pageItem) => (
                  <SelectItem key={pageItem.page.path} value={pageItem.page.path} className="text-xs">
                    <div className="flex items-center gap-2">
                      <span className="font-medium">{pageItem.page.path}</span>
                      {pageItem.page.name && (
                        <span className="text-muted-foreground">· {pageItem.page.name}</span>
                      )}
                      {!pageItem.hasPreviewForPreset && (
                        <Badge variant="secondary" className="ml-1 text-[10px]">
                          {t('plugins.preview.noPreviewBadge')}
                        </Badge>
                      )}
                    </div>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* When editing without a current preview, show page selector based on availablePages */}
          {isEditing && !hasCurrentPreview && availablePages.length > 1 && (
            <Select value={selectedPageForAdd} onValueChange={setSelectedPageForAdd}>
              <SelectTrigger className="h-8 w-[200px] text-xs">
                <div className="flex items-center gap-2">
                  <List className="h-3 w-3" />
                  <SelectValue placeholder={t('plugins.preview.pageSelectPlaceholder')} />
                </div>
              </SelectTrigger>
              <SelectContent>
                {availablePages.map((p: PagePreview) => (
                  <SelectItem key={p.path} value={p.path} className="text-xs">
                    <span className="font-medium">{p.path}</span>
                    {p.name && <span className="text-muted-foreground ml-2">· {p.name}</span>}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          )}

          {/* Show current path if editing an existing preview */}
          {isEditing && hasCurrentPreview && (
            <span className="text-muted-foreground text-sm font-medium">{page?.path || t('plugins.preview.unknownPage')}</span>
          )}

          {isEditing && (
            <Badge variant="secondary" className="text-xs">
              {t('plugins.preview.editingBadge')}
            </Badge>
          )}
        </div>

        <div className="flex items-center gap-3">
          {/* Page Navigation Arrows - Only show when not editing and multiple pages exist */}
          {!isEditing && allAvailablePages.length > 1 && (
            <TooltipProvider>
              <div className="flex items-center gap-1">
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const currentPath = page?.path || allAvailablePages[0]?.page.path;
                        const currentIdx = allAvailablePages.findIndex((p) => p.page.path === currentPath);
                        const prevIdx = currentIdx > 0 ? currentIdx - 1 : allAvailablePages.length - 1;
                        handlePageChange(allAvailablePages[prevIdx].page.path);
                      }}
                      className="h-7 w-7"
                    >
                      <ChevronLeft className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{t('plugins.preview.prevPageTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
                <span className="text-muted-foreground text-xs">
                  {(() => {
                    const currentPath = page?.path || allAvailablePages[0]?.page.path;
                    const currentIdx = allAvailablePages.findIndex((p) => p.page.path === currentPath);
                    return t('plugins.preview.pageCounter', {
                      current: currentIdx + 1,
                      total: allAvailablePages.length,
                    });
                  })()}
                </span>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => {
                        const currentPath = page?.path || allAvailablePages[0]?.page.path;
                        const currentIdx = allAvailablePages.findIndex((p) => p.page.path === currentPath);
                        const nextIdx = currentIdx < allAvailablePages.length - 1 ? currentIdx + 1 : 0;
                        handlePageChange(allAvailablePages[nextIdx].page.path);
                      }}
                      className="h-7 w-7"
                    >
                      <ChevronRight className="h-3.5 w-3.5" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p className="text-xs">{t('plugins.preview.nextPageTooltip')}</p>
                  </TooltipContent>
                </Tooltip>
              </div>
            </TooltipProvider>
          )}

          {/* View Mode Toggle - Only show when has preview and not editing */}
          {hasCurrentPreview && !isEditing && (
            <Tabs value={viewMode} onValueChange={(v) => setViewMode(v as 'desktop' | 'mobile')}>
              <TabsList className="h-8">
                <TabsTrigger value="desktop" className="gap-1.5 text-xs">
                  <Monitor className="h-3 w-3" />
                  {t('plugins.preview.desktopTab')}
                </TabsTrigger>
                <TabsTrigger value="mobile" className="gap-1.5 text-xs">
                  <Smartphone className="h-3 w-3" />
                  {t('plugins.preview.mobileTab')}
                </TabsTrigger>
              </TabsList>
            </Tabs>
          )}

          {/* Edit/Save/Cancel buttons - Only show when editable */}
          {editable && !isEditing && (
            <div className="flex gap-2">
              {hasCurrentPreview && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setIsEditing(true)}
                  className="h-8 gap-2 text-xs"
                >
                  <Edit2 className="h-3 w-3" />
                  {t('common:edit')}
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => {
                  setIsEditing(true);
                  setEditPreviewUrl(''); // Clear URL for new preview
                  if (page) setSelectedPageForAdd(page.path);
                }}
                className="h-8 gap-2 text-xs"
              >
                <Plus className="h-3 w-3" />
                {t('plugins.preview.addPreview')}
              </Button>
            </div>
          )}
        </div>
      </div>

      {/* Preview Content or Edit Form */}
      <div className="flex flex-1 items-center justify-center overflow-hidden bg-gradient-to-br from-slate-50 to-slate-100 p-4 dark:from-slate-900 dark:to-slate-950">
        {isEditing ? (
          /* Edit/Add Form */
          <Card className="w-full max-w-md">
            <CardContent className="space-y-4 pt-6">
              {!hasCurrentPreview && availablePages.length > 1 && (
                <div className="bg-muted/50 rounded-md p-3 text-sm">
                  <p className="text-muted-foreground text-xs">
                    {t('plugins.preview.pageSelectorHint')}
                  </p>
                </div>
              )}

              <div className="space-y-2">
                <Label htmlFor="edit-url">{t('plugins.preview.urlLabel')}</Label>
                <Input
                  id="edit-url"
                  type="url"
                  placeholder="https://example.com/page"
                  value={editPreviewUrl}
                  onChange={(e) => setEditPreviewUrl(e.target.value)}
                  autoFocus
                />
                <p className="text-muted-foreground text-xs">
                  {t('plugins.preview.urlHint')}
                </p>
              </div>

              {/* Action Buttons */}
              <div className="flex justify-center gap-3 pt-2">
                {(hasCurrentPreview || !hasData) && (
                  <Button
                    variant="outline"
                    onClick={handleCancelEdit}
                    disabled={addPreviewMutation.isPending}
                    className="min-w-[120px]"
                  >
                    <X className="mr-2 h-4 w-4" />
                    {t('common:cancel')}
                  </Button>
                )}
                <Button
                  variant="default"
                  onClick={handleSavePreview}
                  disabled={addPreviewMutation.isPending || !editPreviewUrl}
                  className="min-w-[120px]"
                >
                  {addPreviewMutation.isPending ? (
                    <>
                      <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                      {t('common:saving')}
                    </>
                  ) : (
                    <>
                      <Save className="mr-2 h-4 w-4" />
                      {t('common:save')}
                    </>
                  )}
                </Button>
              </div>

              {/* Show current URL and status only when editing an existing preview */}
              {hasCurrentPreview && preview?.previewUrl && (
                <div className="bg-muted/30 space-y-3 rounded-lg border p-3">
                  <div>
                    <p className="text-muted-foreground mb-1 text-xs font-medium">{t('plugins.preview.currentUrl')}</p>
                    <a
                      href={preview?.previewUrl || ''}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-primary flex items-center gap-1 text-sm hover:underline"
                    >
                      {preview?.previewUrl || ''}
                      <ExternalLink className="h-3 w-3" />
                    </a>
                  </div>

                  {/* Screenshot status */}
                  <div className="space-y-2">
                    <p className="text-muted-foreground text-xs font-medium">{t('plugins.preview.screenshotStatus')}</p>
                    <div className="grid grid-cols-2 gap-2">
                      {/* Desktop screenshot status */}
                      <div
                        className={`flex items-center gap-2 rounded-md border p-2 text-xs ${
                          preview.screenshotUrls?.desktop
                            ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
                            : isPendingCapture
                              ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300'
                              : 'border-muted bg-muted/50 text-muted-foreground'
                        }`}
                      >
                        {preview.screenshotUrls?.desktop ? (
                          <>
                            <Monitor className="h-3.5 w-3.5" />
                            <span>{t('plugins.preview.desktopOk')}</span>
                          </>
                        ) : isPendingCapture ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            <span>{t('plugins.preview.statusCapturing')}</span>
                          </>
                        ) : (
                          <>
                            <Monitor className="h-3.5 w-3.5" />
                            <span>{t('plugins.preview.statusPending')}</span>
                          </>
                        )}
                      </div>

                      {/* Mobile screenshot status */}
                      <div
                        className={`flex items-center gap-2 rounded-md border p-2 text-xs ${
                          preview.screenshotUrls?.mobile
                            ? 'border-green-200 bg-green-50 text-green-700 dark:border-green-800 dark:bg-green-950 dark:text-green-300'
                            : isPendingCapture
                              ? 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-300'
                              : 'border-muted bg-muted/50 text-muted-foreground'
                        }`}
                      >
                        {preview.screenshotUrls?.mobile ? (
                          <>
                            <Smartphone className="h-3.5 w-3.5" />
                            <span>{t('plugins.preview.mobileOk')}</span>
                          </>
                        ) : isPendingCapture ? (
                          <>
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                            <span>{t('plugins.preview.statusCapturing')}</span>
                          </>
                        ) : (
                          <>
                            <Smartphone className="h-3.5 w-3.5" />
                            <span>{t('plugins.preview.statusPending')}</span>
                          </>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        ) : screenshotUrl ? (
          <div className="flex h-full w-full items-start justify-center overflow-hidden pt-2">
            {viewMode === 'desktop' ? (
              /* Desktop Browser Mockup - Fixed viewport size */
              <div className="bg-background flex h-[500px] max-h-[calc(100%-1rem)] w-[900px] max-w-[calc(100%-2rem)] flex-col overflow-hidden rounded-lg border shadow-xl">
                {/* Browser Chrome */}
                <div className="bg-muted flex shrink-0 items-center gap-2 border-b px-3 py-2">
                  <div className="flex gap-1">
                    <div className="h-2.5 w-2.5 rounded-full bg-red-500" />
                    <div className="h-2.5 w-2.5 rounded-full bg-yellow-500" />
                    <div className="h-2.5 w-2.5 rounded-full bg-green-500" />
                  </div>
                  <a
                    href={getPreviewUrlWithPreset(preview?.previewUrl || '')}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="bg-background text-muted-foreground hover:text-foreground ml-3 flex flex-1 items-center gap-1 rounded px-2 py-1 text-[10px] transition-colors hover:underline"
                    title={t('plugins.preview.openInNewTab', {
                      url: getPreviewUrlWithPreset(preview?.previewUrl || ''),
                    })}
                  >
                    <span className="truncate">{getPreviewUrlWithPreset(preview?.previewUrl || '')}</span>
                    <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-60" />
                  </a>
                </div>
                {/* Screenshot - scrollable within fixed viewport */}
                <div className="min-h-0 flex-1 overflow-y-auto bg-white">
                  {/* eslint-disable-next-line @next/next/no-img-element */}
                  <img
                    src={screenshotUrl}
                    alt={t('plugins.preview.screenshotAlt', { path: page.path })}
                    className="block w-full"
                  />
                </div>
              </div>
            ) : (
              /* Mobile Browser Mockup - iPhone-like device frame */
              <div className="flex flex-col items-center">
                {/* iPhone Frame */}
                <div className="relative rounded-[40px] border-[12px] border-slate-800 bg-slate-800 shadow-2xl dark:border-slate-700">
                  {/* Dynamic Island / Notch */}
                  <div className="absolute left-1/2 top-2 z-10 h-[25px] w-[90px] -translate-x-1/2 rounded-full bg-slate-900" />
                  {/* Screen */}
                  <div className="flex h-[680px] max-h-[calc(100vh-180px)] w-[320px] flex-col overflow-hidden rounded-[28px] bg-white">
                    {/* Status Bar */}
                    <div className="flex shrink-0 items-center justify-between bg-white px-6 py-2 pt-8">
                      <span className="text-[11px] font-semibold text-black">9:41</span>
                      <div className="flex items-center gap-1">
                        <svg className="h-3.5 w-3.5 text-black" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M12 3c-4.97 0-9 4.03-9 9s4.03 9 9 9c.83 0 1.5-.67 1.5-1.5 0-.39-.15-.74-.39-1.01-.23-.26-.38-.61-.38-.99 0-.83.67-1.5 1.5-1.5H16c2.76 0 5-2.24 5-5 0-4.42-4.03-8-9-8z"/>
                        </svg>
                        <svg className="h-3.5 w-3.5 text-black" fill="currentColor" viewBox="0 0 24 24">
                          <path d="M1 9l2 2c4.97-4.97 13.03-4.97 18 0l2-2C16.93 2.93 7.08 2.93 1 9zm8 8l3 3 3-3c-1.65-1.66-4.34-1.66-6 0zm-4-4l2 2c2.76-2.76 7.24-2.76 10 0l2-2C15.14 9.14 8.87 9.14 5 13z"/>
                        </svg>
                        <svg className="h-5 w-3 text-black" fill="currentColor" viewBox="0 0 12 20">
                          <rect x="0" y="2" width="10" height="16" rx="2" stroke="currentColor" strokeWidth="1" fill="none"/>
                          <rect x="10" y="7" width="2" height="5" rx="0.5" fill="currentColor"/>
                          <rect x="2" y="4" width="6" height="12" rx="1" fill="currentColor"/>
                        </svg>
                      </div>
                    </div>
                    {/* URL Bar */}
                    <div className="shrink-0 bg-slate-50 px-3 py-1.5">
                      <a
                        href={getPreviewUrlWithPreset(preview?.previewUrl || '')}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center justify-center gap-1 rounded-lg bg-slate-200/80 px-3 py-1.5 text-[11px] text-slate-600 transition-colors hover:bg-slate-300/80"
                        title={t('plugins.preview.openInNewTab', {
                      url: getPreviewUrlWithPreset(preview?.previewUrl || ''),
                    })}
                      >
                        <span className="max-w-[200px] truncate">{new URL(getPreviewUrlWithPreset(preview?.previewUrl || 'https://example.com')).hostname}</span>
                        <ExternalLink className="h-2.5 w-2.5 shrink-0 opacity-60" />
                      </a>
                    </div>
                    {/* Content - scrollable */}
                    <div
                      ref={mobileScrollRef}
                      className="min-h-0 flex-1 overflow-y-auto bg-white"
                    >
                      {/* eslint-disable-next-line @next/next/no-img-element */}
                      <img
                        src={screenshotUrl}
                        alt={t('plugins.preview.screenshotAlt', { path: page.path })}
                        className="block w-full"
                      />
                    </div>
                    {/* Home Indicator */}
                    <div className="flex shrink-0 justify-center bg-white pb-2 pt-1">
                      <div className="h-1 w-28 rounded-full bg-slate-300" />
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        ) : (
          /* No Screenshot Available or Loading */
          <div className="flex flex-col items-center gap-4 text-center">
            {isPendingCapture ? (
              /* Loading State */
              <>
                <div className="bg-primary/10 flex h-20 w-20 animate-pulse items-center justify-center rounded-full">
                  <Loader2 className="text-primary h-10 w-10 animate-spin" />
                </div>
                <div>
                  <h3 className="font-semibold">{t('plugins.preview.capturingTitle')}</h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('plugins.preview.capturingForViewMode', {
                      mode: t(
                        viewMode === 'desktop'
                          ? 'plugins.preview.modeDesktop'
                          : 'plugins.preview.modeMobile',
                      ),
                    })}
                  </p>
                  <p className="text-muted-foreground mt-2 text-xs">
                    {t('plugins.preview.capturingTiming')}
                    <span className="ml-2 inline-block h-1.5 w-1.5 animate-pulse rounded-full bg-green-500"></span>
                    <span className="ml-0.5 text-[10px] text-green-600">{t('plugins.preview.capturingCheckingUpdates')}</span>
                  </p>
                  {editable && preview?.metadata?.status === 'pending' && (
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={handleRemovePendingPreview}
                      disabled={removePreviewMutation.isPending}
                      className="mt-4 gap-2"
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                      {removePreviewMutation.isPending ? t('plugins.preview.capturingRemoving') : t('common:cancel')}
                    </Button>
                  )}
                </div>
              </>
            ) : (
              /* No Screenshot State */
              <>
                <div className="bg-muted flex h-16 w-16 items-center justify-center rounded-full">
                  <ImageIcon className="text-muted-foreground h-8 w-8" />
                </div>
                <div>
                  <h3 className="font-semibold">{t('plugins.preview.noScreenshotTitle')}</h3>
                  <p className="text-muted-foreground mt-1 text-sm">
                    {t('plugins.preview.noScreenshotForViewMode', {
                      mode: t(
                        viewMode === 'desktop'
                          ? 'plugins.preview.modeDesktop'
                          : 'plugins.preview.modeMobile',
                      ),
                    })}
                  </p>
                  {editable && (
                    <p className="text-muted-foreground mt-2 text-xs">
                      {t('plugins.preview.noScreenshotHint')}
                    </p>
                  )}
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
